import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Skeleton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Block,
  ArrowBackRounded,
  MoreHoriz,
  DeleteOutline,
  LibraryBooksOutlined,
  MenuBookOutlined,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useAuth } from '../../context/AuthContext';
import { useDebounce } from '../../hooks/useDebounce';
import {
  questionPaperService,
  QuestionBank,
  Chapter,
  Question,
  QuestionType,
  QuestionImportance,
  QuestionDifficulty,
  QuestionBankFormData,
  ChapterFormData,
  QuestionFormData,
} from '../../services/questionPaperService';
import { centerService } from '../../services/centerService';
import { classService } from '../../services/classService';
import { subjectService } from '../../services/subjectService';
import { teachingLoadsApi } from '../../services/sessions/teachingLoadsApi';
import {
  QuestionBankFilters,
  ChapterSidebar,
  QuestionPane,
  CreateBankDialog,
  ChapterDialog,
  QuestionDialog,
  ViewQuestionDialog,
  CSVUploadDialog,
  Class,
  Subject,
  Center,
  PaginationState,
  CSVUploadResults,
  GetImportanceColorFn,
  SearchScope,
} from '../../components/questionBank';
import BankOverview, { nameOf } from '../../components/questionBank/BankOverview';
import { fmtClassName } from '../../utils/pdf/format';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import EmptyState from '../../components/common/EmptyState';

/** Banks per page on the overview. */
const BANKS_PAGE = 24;

const QuestionBankPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { canRead, canWrite } = useModulePermissions();
  const { user, decodedToken } = useAuth();

  // Plain-teacher scope — mirrors the backend util. True iff the user holds
  // the system Teacher role AND is not elevated to a school / system admin.
  // Admins see every bank + every class / subject in the dropdowns.
  const isRestrictedTeacher = !!decodedToken?.isTeacher
    && !decodedToken?.isSchoolAdmin
    && !decodedToken?.isSystemAdmin;

  // (classId, subjectId) pairs the teacher is assigned to teach in the
  // active session. Populated once on mount for teachers; stays empty for
  // admins. Drives the dropdown filters below. Shared pattern with
  // GenerateQuestionPaperPage — same /teaching-loads/mine endpoint.
  const [teacherPairs, setTeacherPairs] = useState<Array<{ classId: string; subjectId: string }>>([]);

  // State
  const [questionBanks, setQuestionBanks] = useState<QuestionBank[]>([]);
  const [bankLoading, setBankLoading] = useState(false);
  // Overview: every bank the user can open, a page at a time.
  const [overviewBanks, setOverviewBanks] = useState<QuestionBank[]>([]);
  const [overviewTotal, setOverviewTotal] = useState(0);
  const [overviewPage, setOverviewPage] = useState(1);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewLoadingMore, setOverviewLoadingMore] = useState(false);
  const [bankMenuAnchor, setBankMenuAnchor] = useState<HTMLElement | null>(null);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null);
  // Questions for the selected chapter
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  // A request is in flight (shown in the search field without blanking the list).
  const [searching, setSearching] = useState(false);
  // Only the newest request may update the list; a slower older one is dropped.
  const questionsRequest = useRef(0);
  // Where a search looks: the open chapter, or every chapter of the bank.
  const [searchScope, setSearchScope] = useState<SearchScope>('chapter');
  const searchScopeRef = useRef<SearchScope>('chapter');
  const [pagination, setPagination] = useState<PaginationState>({ page: 1, limit: 10, total: 0 });

  // Filters
  const [centers, setCenters] = useState<Center[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedCenter, setSelectedCenter] = useState('');
  // The open bank lives in the URL (?class=&subject=), so reload and Back keep it.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedClass = searchParams.get('class') || '';
  const selectedSubject = searchParams.get('subject') || '';
  const openScope = useCallback((classId: string, subjectId: string) => {
    const next = new URLSearchParams();
    if (classId) next.set('class', classId);
    if (subjectId) next.set('subject', subjectId);
    setSearchParams(next);
  }, [setSearchParams]);
  // Question filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterImportance, setFilterImportance] = useState('');

  // Debounced search query
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Modal-specific state for creating question banks
  const [modalSubjects, setModalSubjects] = useState<Subject[]>([]);

  // Dialogs
  const [createBankOpen, setCreateBankOpen] = useState(false);
  const [createChapterOpen, setCreateChapterOpen] = useState(false);
  const [editChapterOpen, setEditChapterOpen] = useState(false);
  const [createQuestionOpen, setCreateQuestionOpen] = useState(false);
  const [editQuestionOpen, setEditQuestionOpen] = useState(false);
  const [viewQuestionOpen, setViewQuestionOpen] = useState(false);
  const [uploadCSVOpen, setUploadCSVOpen] = useState(false);
  const [csvUploadChapter, setCsvUploadChapter] = useState<Chapter | null>(null);

  // Form data
  const [bankFormData, setBankFormData] = useState<QuestionBankFormData>({
    classId: '',
    subjectId: '',
  });
  const [chapterFormData, setChapterFormData] = useState<ChapterFormData>({
    questionBankId: '',
    name: '',
    description: '',
    order: 0,
  });
  const [questionFormData, setQuestionFormData] = useState<QuestionFormData>({
    chapterId: '',
    questionText: '',
    type: QuestionType.MCQ,
    importance: QuestionImportance.MEDIUM,
    options: ['', '', '', ''],
    correctAnswer: '',
    answer: '',
    answerImages: [],
    questionImages: [],
    metadata: {
      tags: [],
      pastPaperReferences: [],
      marks: undefined,
      difficulty: QuestionDifficulty.EASY,
      estimatedTime: undefined,
    },
  });
  const [tagsInput, setTagsInput] = useState('');
  const [pastPapersInput, setPastPapersInput] = useState('');
  const [editingChapter, setEditingChapter] = useState<Chapter | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [viewingQuestion, setViewingQuestion] = useState<Question | null>(null);

  // CSV upload state
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResults, setCsvResults] = useState<CSVUploadResults | null>(null);

  // Delete Dialog State
  const [deleteBankDialogOpen, setDeleteBankDialogOpen] = useState(false);
  const [bankToDelete, setBankToDelete] = useState<QuestionBank | null>(null);
  const [deleteChapterDialogOpen, setDeleteChapterDialogOpen] = useState(false);
  const [chapterToDelete, setChapterToDelete] = useState<Chapter | null>(null);
  const [deleteQuestionDialogOpen, setDeleteQuestionDialogOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<Question | null>(null);

  const isSystemAdmin = user?.isSystemAdmin;

  // The active question bank (first matching bank for selected class+subject)
  const activeBank = useMemo(() => {
    return questionBanks.length > 0 ? questionBanks[0] : null;
  }, [questionBanks]);

  const activeBankId = useMemo(() => {
    if (!activeBank) return null;
    return typeof activeBank._id === 'string' ? activeBank._id : (activeBank._id as any)._id || activeBank._id;
  }, [activeBank]);

  // ─── Teacher-scoped dropdown options ───────────────────────────────────
  // Same shape as GenerateQuestionPaperPage — admins see everything, plain
  // teachers only see classes they teach + subjects paired with the picked
  // class. Filter + create-modal dropdowns both pull from these memos.
  const allowedClassIdsSet = useMemo(
    () => new Set(teacherPairs.map(p => p.classId)),
    [teacherPairs],
  );

  const subjectIdsForClass = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const p of teacherPairs) {
      if (!map.has(p.classId)) map.set(p.classId, new Set());
      map.get(p.classId)!.add(p.subjectId);
    }
    return map;
  }, [teacherPairs]);

  const allSubjectIdsTaughtByMe = useMemo(
    () => new Set(teacherPairs.map(p => p.subjectId)),
    [teacherPairs],
  );

  // List-filter dropdowns.
  const visibleFilterClasses = useMemo(() => (
    isRestrictedTeacher ? classes.filter(c => allowedClassIdsSet.has(c._id)) : classes
  ), [classes, isRestrictedTeacher, allowedClassIdsSet]);

  const visibleFilterSubjects = useMemo(() => {
    if (!isRestrictedTeacher) return subjects;
    const allowedIds = selectedClass
      ? (subjectIdsForClass.get(selectedClass) ?? new Set<string>())
      : allSubjectIdsTaughtByMe;
    return subjects.filter(s => allowedIds.has(s._id));
  }, [subjects, isRestrictedTeacher, subjectIdsForClass, allSubjectIdsTaughtByMe, selectedClass]);

  // Create-bank modal: class dropdown restricts to the teacher's classes.
  // (The subject dropdown is already narrowed to the picked class by the
  // existing fetchModalSubjects call, so we only need to intersect that
  // result with the teacher's subjectIdsForClass map before rendering.)
  const visibleModalClasses = useMemo(() => (
    isRestrictedTeacher ? classes.filter(c => allowedClassIdsSet.has(c._id)) : classes
  ), [classes, isRestrictedTeacher, allowedClassIdsSet]);

  const visibleModalSubjects = useMemo(() => {
    if (!isRestrictedTeacher) return modalSubjects;
    const allowedIds = subjectIdsForClass.get(bankFormData.classId) ?? new Set<string>();
    return modalSubjects.filter(s => allowedIds.has(s._id));
  }, [modalSubjects, isRestrictedTeacher, subjectIdsForClass, bankFormData.classId]);

  // Memoized utility functions
  const getImportanceColor = useCallback<GetImportanceColorFn>((importance) => {
    switch (importance) {
      case QuestionImportance.HIGH:
        return 'error';
      case QuestionImportance.MEDIUM:
        return 'warning';
      case QuestionImportance.LOW:
        return 'success';
      default:
        return 'default';
    }
  }, []);

  // === Fetch functions ===

  const fetchCenters = useCallback(async () => {
    if (!isSystemAdmin) return;
    try {
      const { centers: centersData } = await centerService.getCenters();
      setCenters(centersData || []);
    } catch (error) {
      console.error('Error fetching centers:', error);
    }
  }, [isSystemAdmin]);

  const fetchClasses = useCallback(async () => {
    try {
      const params: any = { isActive: true, limit: 100 };
      if (selectedCenter && isSystemAdmin) {
        params.centerId = selectedCenter;
      }
      const { classes: classesData } = await classService.getClasses(params);
      setClasses(classesData || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  }, [selectedCenter, isSystemAdmin]);

  const fetchSubjects = useCallback(async () => {
    if (!selectedClass) return;
    try {
      // Subjects are school-wide; class-specific filtering is a Curriculum
      // concern. Question Bank lists every subject the school manages.
      const { subjects: subjectsData } = await subjectService.getSubjects({
        isActive: true,
        limit: 100,
      });
      setSubjects(subjectsData || []);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  }, [selectedClass]);

  const fetchQuestionBanks = useCallback(async () => {
    setBankLoading(true);
    try {
      const params: any = {};
      if (selectedCenter && isSystemAdmin) params.schoolId = selectedCenter;
      if (selectedClass) params.classId = selectedClass;
      if (selectedSubject) params.subjectId = selectedSubject;

      const { questionBanks: banks } = await questionPaperService.getQuestionBanks(params);
      setQuestionBanks(banks);
    } catch (error) {
      console.error('Error fetching question banks:', error);
      enqueueSnackbar('Failed to fetch question banks', { variant: 'error' });
    } finally {
      setBankLoading(false);
    }
  }, [selectedCenter, selectedClass, selectedSubject, isSystemAdmin, enqueueSnackbar]);

  // Overview — page 1 replaces the list, later pages append.
  const fetchOverview = useCallback(async (page: number) => {
    if (page === 1) setOverviewLoading(true); else setOverviewLoadingMore(true);
    try {
      const params: any = { page, limit: BANKS_PAGE };
      if (selectedCenter && isSystemAdmin) params.schoolId = selectedCenter;
      if (selectedClass) params.classId = selectedClass;
      const { questionBanks: banks, pagination: pag } = await questionPaperService.getQuestionBanks(params);
      setOverviewBanks((prev) => (page === 1 ? banks : [...prev, ...banks]));
      setOverviewTotal(pag.totalItems || 0);
      setOverviewPage(page);
    } catch (error) {
      console.error('Error fetching question banks:', error);
      enqueueSnackbar('Failed to fetch question banks', { variant: 'error' });
    } finally {
      setOverviewLoading(false);
      setOverviewLoadingMore(false);
    }
  }, [selectedCenter, selectedClass, isSystemAdmin, enqueueSnackbar]);

  const fetchChapters = useCallback(async (questionBankId: string) => {
    setChaptersLoading(true);
    try {
      const chaptersData = await questionPaperService.getChapters(questionBankId);
      setChapters(chaptersData);
    } catch (error) {
      console.error('Error fetching chapters:', error);
      enqueueSnackbar('Failed to fetch chapters', { variant: 'error' });
    } finally {
      setChaptersLoading(false);
    }
  }, [enqueueSnackbar]);

  const fetchModalSubjects = useCallback(async (classId: string) => {
    if (!classId) {
      setModalSubjects([]);
      return;
    }
    try {
      const { subjects: subjectsData } = await subjectService.getSubjects({
        isActive: true,
        limit: 100,
      });
      setModalSubjects(subjectsData || []);
    } catch (error) {
      console.error('Error fetching modal subjects:', error);
    }
  }, []);

  const fetchQuestions = useCallback(async (
    chapterId: string,
    page: number = 1,
    search?: string,
    type?: string,
    importance?: string,
    limit?: number,
    showLoader: boolean = true,
  ) => {
    const request = ++questionsRequest.current;
    if (showLoader) setQuestionsLoading(true);
    setSearching(true);
    try {
      const params: any = { page, limit: limit || pagination.limit };
      if (type) params.type = type;
      if (importance) params.importance = importance;
      if (search) params.search = search;

      const bankWide = !!search?.trim() && searchScopeRef.current === 'bank' && !!activeBankId;
      const { questions: qs, pagination: pag } = bankWide
        ? await questionPaperService.getQuestionsByBank(activeBankId as string, params)
        : await questionPaperService.getQuestions(chapterId, params);
      if (request !== questionsRequest.current) return;
      setQuestions(qs);
      setPagination({ page, limit: limit || pagination.limit, total: pag.totalItems });
    } catch (error: any) {
      if (request !== questionsRequest.current) return;
      console.error('Error fetching questions:', error);
      enqueueSnackbar(error?.message || 'Failed to fetch questions', { variant: 'error' });
    } finally {
      if (request === questionsRequest.current) {
        setQuestionsLoading(false);
        setSearching(false);
      }
    }
  }, [pagination.limit, activeBankId, enqueueSnackbar]);

  // === Effects ===

  // Fetch initial data
  useEffect(() => {
    fetchCenters();
    fetchClasses();
  }, [fetchCenters, fetchClasses]);

  // The overview shows whenever a bank isn't fully picked.
  const inBank = !!(selectedClass && selectedSubject);
  useEffect(() => {
    if (!inBank) fetchOverview(1);
  }, [inBank, fetchOverview]);

  // Load the teacher's current-session (class, subject) assignments once on
  // mount. Reuses /teaching-loads/mine same as the paper builder — single
  // source of truth, DRY. Admins skip the call entirely.
  useEffect(() => {
    if (!isRestrictedTeacher) return;
    let cancelled = false;
    (async () => {
      try {
        const mine = await teachingLoadsApi.getMine();
        if (cancelled) return;
        const seen = new Set<string>();
        const pairs: Array<{ classId: string; subjectId: string }> = [];
        for (const row of mine.subjectsTaught || []) {
          const key = `${row.classId}:${row.subjectId}`;
          if (seen.has(key)) continue;
          seen.add(key);
          pairs.push({ classId: row.classId, subjectId: row.subjectId });
        }
        setTeacherPairs(pairs);
      } catch (err) {
        console.error('Failed to fetch teacher assignments:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isRestrictedTeacher]);

  // Fetch subjects when class changes
  useEffect(() => {
    if (selectedClass) {
      fetchSubjects();
    } else {
      setSubjects([]);
    }
  }, [selectedClass, fetchSubjects]);

  // Fetch question banks when BOTH class and subject are selected
  useEffect(() => {
    if (selectedClass && selectedSubject) {
      fetchQuestionBanks();
    } else {
      setQuestionBanks([]);
    }
  }, [selectedClass, selectedSubject, fetchQuestionBanks]);

  // Auto-load chapters when a bank is found
  useEffect(() => {
    if (activeBankId) {
      fetchChapters(activeBankId);
    } else {
      setChapters([]);
    }
    // Reset selected chapter & questions
    setSelectedChapter(null);
    setQuestions([]);
    setPagination({ page: 1, limit: 10, total: 0 });
    setSearchQuery('');
    setFilterType('');
    setFilterImportance('');
    searchScopeRef.current = 'chapter';
    setSearchScope('chapter');
  }, [activeBankId, fetchChapters]);

  // Open the first chapter as soon as a bank's chapters arrive, so a bank
  // never lands on an empty "pick a chapter" screen.
  useEffect(() => {
    if (chaptersLoading || chapters.length === 0) return;
    if (selectedChapter && chapters.some(c => c._id === selectedChapter._id)) return;
    setSelectedChapter(chapters[0]);
    fetchQuestions(chapters[0]._id, 1, '', '', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapters, chaptersLoading]);

  // Fetch questions when debounced search changes
  useEffect(() => {
    if (selectedChapter) {
      fetchQuestions(selectedChapter._id, 1, debouncedSearchQuery, filterType, filterImportance, undefined, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  // === Handlers ===

  // Chapter selection
  const handleSelectChapter = useCallback((chapter: Chapter) => {
    // Skip if same chapter is already selected
    if (selectedChapter?._id === chapter._id) return;
    setSelectedChapter(chapter);
    // Reset filters for new chapter
    searchScopeRef.current = 'chapter';
    setSearchScope('chapter');
    setSearchQuery('');
    setFilterType('');
    setFilterImportance('');
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchQuestions(chapter._id, 1, '', '', '');
  }, [selectedChapter, fetchQuestions]);

  // Question filter handlers
  const handleSearchChange = useCallback((_chapterId: string, value: string) => {
    setSearchQuery(value);
    // Debounced fetch is handled by useEffect
  }, []);

  const handleTypeChange = useCallback((_chapterId: string, value: string) => {
    setFilterType(value);
    if (selectedChapter) {
      fetchQuestions(selectedChapter._id, 1, searchQuery, value, filterImportance, undefined, false);
    }
  }, [selectedChapter, searchQuery, filterImportance, fetchQuestions]);

  const handleImportanceChange = useCallback((_chapterId: string, value: string) => {
    setFilterImportance(value);
    if (selectedChapter) {
      fetchQuestions(selectedChapter._id, 1, searchQuery, filterType, value, undefined, false);
    }
  }, [selectedChapter, searchQuery, filterType, fetchQuestions]);

  const handleClearFilters = useCallback((_chapterId: string) => {
    setSearchQuery('');
    setFilterType('');
    setFilterImportance('');
    if (selectedChapter) {
      fetchQuestions(selectedChapter._id, 1, '', '', '', undefined, false);
    }
  }, [selectedChapter, fetchQuestions]);

  const handleScopeChange = useCallback((scope: SearchScope) => {
    searchScopeRef.current = scope;
    setSearchScope(scope);
    if (selectedChapter) {
      fetchQuestions(selectedChapter._id, 1, searchQuery, filterType, filterImportance, undefined, false);
    }
  }, [selectedChapter, searchQuery, filterType, filterImportance, fetchQuestions]);

  const handlePageChange = useCallback((_chapterId: string, page: number) => {
    if (selectedChapter) {
      fetchQuestions(selectedChapter._id, page, searchQuery, filterType, filterImportance, undefined, false);
    }
  }, [selectedChapter, searchQuery, filterType, filterImportance, fetchQuestions]);

  const handleRowsPerPageChange = useCallback((_chapterId: string, limit: number) => {
    setPagination(prev => ({ ...prev, limit }));
    if (selectedChapter) {
      fetchQuestions(selectedChapter._id, 1, searchQuery, filterType, filterImportance, limit, false);
    }
  }, [selectedChapter, searchQuery, filterType, filterImportance, fetchQuestions]);

  // Center/Class/Subject filter handlers
  const handleCenterChange = useCallback((value: string) => {
    setSelectedCenter(value);
    openScope('', '');
  }, [openScope]);

  const handleClassChange = useCallback((value: string) => {
    openScope(value, '');
  }, [openScope]);

  const handleSubjectChange = useCallback((value: string) => {
    openScope(selectedClass, value);
  }, [openScope, selectedClass]);

  const handleOpenBank = useCallback((bank: QuestionBank) => {
    openScope(
      typeof bank.classId === 'string' ? bank.classId : bank.classId._id,
      typeof bank.subjectId === 'string' ? bank.subjectId : bank.subjectId._id,
    );
  }, [openScope]);

  // Bank handlers
  const handleCreateBank = () => {
    setBankFormData({ classId: selectedClass, subjectId: '' });
    if (selectedClass) fetchModalSubjects(selectedClass); else setModalSubjects([]);
    setCreateBankOpen(true);
  };

  // The class and subject are already picked, so no dialog is needed.
  const [creatingHere, setCreatingHere] = useState(false);
  const handleCreateBankHere = async () => {
    setCreatingHere(true);
    try {
      await questionPaperService.createQuestionBank({ classId: selectedClass, subjectId: selectedSubject });
      enqueueSnackbar('Question bank created', { variant: 'success' });
      fetchQuestionBanks();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || error.message || 'Failed to create question bank', { variant: 'error' });
    } finally {
      setCreatingHere(false);
    }
  };

  const handleSubmitBank = async () => {
    if (!bankFormData.classId || !bankFormData.subjectId) {
      enqueueSnackbar('Please select class and subject', { variant: 'warning' });
      return;
    }

    try {
      await questionPaperService.createQuestionBank(bankFormData);
      enqueueSnackbar('Question bank created', { variant: 'success' });
      setCreateBankOpen(false);
      openScope(bankFormData.classId, bankFormData.subjectId);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to create question bank', { variant: 'error' });
    }
  };

  const handleDeleteBank = useCallback((bankId: string) => {
    const bank = questionBanks.find(b => {
      const id = typeof b._id === 'string' ? b._id : (b._id as any)._id || b._id;
      return id === bankId;
    });
    if (bank) {
      setBankToDelete(bank);
      setDeleteBankDialogOpen(true);
    }
  }, [questionBanks]);

  const confirmDeleteBank = async () => {
    if (!bankToDelete) return;
    const bankId = typeof bankToDelete._id === 'string' ? bankToDelete._id : (bankToDelete._id as any)._id || bankToDelete._id;

    try {
      await questionPaperService.deleteQuestionBank(bankId);
      enqueueSnackbar('Question bank deleted', { variant: 'success' });
      openScope('', '');
    } catch (error) {
      enqueueSnackbar('Failed to delete question bank', { variant: 'error' });
    } finally {
      setDeleteBankDialogOpen(false);
      setBankToDelete(null);
    }
  };

  // Chapter handlers
  const handleCreateChapter = useCallback((bank: QuestionBank) => {
    const bankId = typeof bank._id === 'string' ? bank._id : (bank._id as any)._id || bank._id;
    setChapterFormData({
      questionBankId: bankId,
      name: '',
      description: '',
      order: chapters.length + 1,
    });
    setCreateChapterOpen(true);
  }, [chapters.length]);

  const handleSubmitChapter = async () => {
    if (!chapterFormData.name.trim()) {
      enqueueSnackbar('Please enter chapter name', { variant: 'warning' });
      return;
    }

    try {
      await questionPaperService.createChapter(chapterFormData);
      enqueueSnackbar('Chapter created successfully', { variant: 'success' });
      setCreateChapterOpen(false);
      if (activeBankId) {
        fetchChapters(activeBankId);
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to create chapter', { variant: 'error' });
    }
  };

  const handleEditChapter = useCallback((chapter: Chapter) => {
    setEditingChapter(chapter);
    setChapterFormData({
      questionBankId: chapter.questionBankId,
      name: chapter.name,
      description: chapter.description || '',
      order: chapter.order,
    });
    setEditChapterOpen(true);
  }, []);

  const handleUpdateChapter = async () => {
    if (!editingChapter || !chapterFormData.name.trim()) {
      enqueueSnackbar('Please enter chapter name', { variant: 'warning' });
      return;
    }

    try {
      await questionPaperService.updateChapter(editingChapter._id, chapterFormData);
      enqueueSnackbar('Chapter updated successfully', { variant: 'success' });
      setEditChapterOpen(false);
      setEditingChapter(null);
      if (activeBankId) {
        fetchChapters(activeBankId);
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to update chapter', { variant: 'error' });
    }
  };

  const handleDeleteChapter = useCallback((chapterId: string) => {
    const chapter = chapters.find(c => c._id === chapterId);
    if (chapter) {
      setChapterToDelete(chapter);
      setDeleteChapterDialogOpen(true);
    }
  }, [chapters]);

  const confirmDeleteChapter = async () => {
    if (!chapterToDelete) return;

    try {
      await questionPaperService.deleteChapter(chapterToDelete._id);
      enqueueSnackbar('Chapter deleted successfully', { variant: 'success' });
      // If deleting the selected chapter, clear selection
      if (selectedChapter && selectedChapter._id === chapterToDelete._id) {
        setSelectedChapter(null);
        setQuestions([]);
        setPagination({ page: 1, limit: 10, total: 0 });
      }
      if (activeBankId) {
        fetchChapters(activeBankId);
      }
    } catch (error) {
      enqueueSnackbar('Failed to delete chapter', { variant: 'error' });
    } finally {
      setDeleteChapterDialogOpen(false);
      setChapterToDelete(null);
    }
  };

  // Question handlers
  const handleCreateQuestion = useCallback((chapter: Chapter) => {
    setQuestionFormData({
      chapterId: chapter._id,
      questionText: '',
      type: QuestionType.MCQ,
      importance: QuestionImportance.MEDIUM,
      options: ['', '', '', ''],
      correctAnswer: '',
      answer: '',
      answerImages: [],
      metadata: {
        tags: [],
        pastPaperReferences: [],
        marks: undefined,
        difficulty: QuestionDifficulty.EASY,
        estimatedTime: undefined,
      },
    });
    setTagsInput('');
    setPastPapersInput('');
    setCreateQuestionOpen(true);
  }, []);

  const handleSubmitQuestion = async () => {
    if (!questionFormData.questionText.trim()) {
      enqueueSnackbar('Please enter question text', { variant: 'warning' });
      return;
    }

    if (questionFormData.type === QuestionType.MCQ) {
      const validOptions = questionFormData.options?.filter(opt => opt.trim()) || [];
      if (validOptions.length < 2) {
        enqueueSnackbar('MCQ must have at least 2 options', { variant: 'warning' });
        return;
      }
    }

    try {
      const data = { ...questionFormData };

      if (data.type === QuestionType.MCQ) {
        data.options = data.options?.filter(opt => opt.trim());
        delete data.answer;
        // answerImages belong to Short/Long — never persist them for other types.
        delete data.answerImages;
        delete data.subParts;
      } else if (data.type === QuestionType.FILL_IN_THE_BLANKS) {
        delete data.options;
        delete data.answer;
        delete data.answerImages;
        delete data.subParts;
      } else {
        delete data.options;
        delete data.correctAnswer;
        if (data.subParts) {
          data.subParts = data.subParts.filter(sp => sp.text.trim());
          if (data.subParts.length === 0) delete data.subParts;
        }
      }

      await questionPaperService.createQuestion(data);
      enqueueSnackbar('Question created successfully', { variant: 'success' });
      setCreateQuestionOpen(false);
      setTagsInput('');
      setPastPapersInput('');
      if (selectedChapter) {
        fetchQuestions(selectedChapter._id, 1, searchQuery, filterType, filterImportance);
      }
      // Refresh chapters to update question count
      if (activeBankId) {
        fetchChapters(activeBankId);
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to create question', { variant: 'error' });
    }
  };

  const handleEditQuestion = useCallback((question: Question) => {
    setEditingQuestion(question);
    setQuestionFormData({
      chapterId: typeof question.chapterId === 'string' ? question.chapterId : question.chapterId._id,
      questionText: question.questionText,
      type: question.type,
      importance: question.importance,
      options: question.options || ['', '', '', ''],
      correctAnswer: question.correctAnswer || '',
      answer: question.answer || '',
      answerImages: question.answerImages || [],
      questionImages: question.questionImages || [],
      subParts: question.subParts && question.subParts.length > 0 ? question.subParts : undefined,
      metadata: question.metadata || {
        tags: [],
        pastPaperReferences: [],
        marks: undefined,
        difficulty: QuestionDifficulty.EASY,
        estimatedTime: undefined,
      },
    });
    setTagsInput(question.metadata?.tags?.join(', ') || '');
    setPastPapersInput(question.metadata?.pastPaperReferences?.join(', ') || '');
    setEditQuestionOpen(true);
  }, []);

  const handleUpdateQuestion = async () => {
    if (!editingQuestion || !questionFormData.questionText.trim()) {
      enqueueSnackbar('Please enter question text', { variant: 'warning' });
      return;
    }

    try {
      const data = { ...questionFormData };

      if (data.type === QuestionType.MCQ) {
        data.options = data.options?.filter(opt => opt.trim());
        delete data.answer;
        delete data.answerImages;
        delete data.subParts;
      } else if (data.type === QuestionType.FILL_IN_THE_BLANKS) {
        delete data.options;
        delete data.answer;
        delete data.answerImages;
        delete data.subParts;
      } else {
        delete data.options;
        delete data.correctAnswer;
        if (data.subParts) {
          data.subParts = data.subParts.filter(sp => sp.text.trim());
          if (data.subParts.length === 0) delete data.subParts;
        }
      }

      await questionPaperService.updateQuestion(editingQuestion._id, data);
      enqueueSnackbar('Question updated successfully', { variant: 'success' });
      setEditQuestionOpen(false);
      setEditingQuestion(null);
      setTagsInput('');
      setPastPapersInput('');
      if (selectedChapter) {
        fetchQuestions(selectedChapter._id, pagination.page, searchQuery, filterType, filterImportance);
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to update question', { variant: 'error' });
    }
  };

  const handleDeleteQuestion = useCallback((questionId: string) => {
    const q = questions.find(q => q._id === questionId);
    if (q) {
      setQuestionToDelete(q);
      setDeleteQuestionDialogOpen(true);
    }
  }, [questions]);

  const confirmDeleteQuestion = async () => {
    if (!questionToDelete) return;

    try {
      await questionPaperService.deleteQuestion(questionToDelete._id);
      enqueueSnackbar('Question deleted successfully', { variant: 'success' });
      if (selectedChapter) {
        fetchQuestions(selectedChapter._id, pagination.page, searchQuery, filterType, filterImportance);
      }
      // Refresh chapters to update question count
      if (activeBankId) {
        fetchChapters(activeBankId);
      }
    } catch (error: any) {
      // Surface the real backend message (e.g. the 409 "used in published
      // paper(s)" explanation). For non-axios errors, fall back to the
      // generic copy.
      const backendMessage = error?.response?.data?.message;
      enqueueSnackbar(backendMessage || error?.message || 'Failed to delete question', {
        variant: 'error',
        // Longer-running toast so the user can read the paper names.
        autoHideDuration: backendMessage ? 8000 : 5000,
      });
    } finally {
      setDeleteQuestionDialogOpen(false);
      setQuestionToDelete(null);
    }
  };

  const handleViewQuestion = useCallback((question: Question) => {
    setViewingQuestion(question);
    setViewQuestionOpen(true);
  }, []);

  // CSV Handlers
  const handleDownloadSampleCSV = async () => {
    try {
      await questionPaperService.downloadSampleCSV();
      enqueueSnackbar('Sample CSV downloaded successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to download sample CSV', { variant: 'error' });
    }
  };

  const handleOpenCSVUpload = useCallback((chapter: Chapter) => {
    setCsvUploadChapter(chapter);
    setCsvFile(null);
    setCsvResults(null);
    setUploadCSVOpen(true);
  }, []);

  const handleCSVFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        enqueueSnackbar('Please select a CSV file', { variant: 'warning' });
        return;
      }
      setCsvFile(file);
      setCsvResults(null);
    }
  };

  const handleUploadCSV = async () => {
    if (!csvFile || !csvUploadChapter) {
      enqueueSnackbar('Please select a CSV file', { variant: 'warning' });
      return;
    }

    setCsvUploading(true);
    try {
      const results = await questionPaperService.uploadQuestionsCSV(csvUploadChapter._id, csvFile);

      if (results) {
        setCsvResults(results);

        if (results.successful.length > 0) {
          enqueueSnackbar(
            `Successfully uploaded ${results.successful.length} question(s)`,
            { variant: 'success' }
          );
          // Refresh questions if the upload chapter is the selected chapter
          if (selectedChapter && selectedChapter._id === csvUploadChapter._id) {
            await fetchQuestions(selectedChapter._id, 1, searchQuery, filterType, filterImportance);
          }
          // Refresh chapters to update question count
          if (activeBankId) {
            fetchChapters(activeBankId);
          }
        }

        if (results.failed.length > 0) {
          enqueueSnackbar(
            `${results.failed.length} question(s) failed to upload. See details below.`,
            { variant: 'warning' }
          );
        }
      }
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to upload CSV', { variant: 'error' });
    } finally {
      setCsvUploading(false);
    }
  };

  const handleCloseCSVUpload = () => {
    setUploadCSVOpen(false);
    setCsvUploadChapter(null);
    setCsvFile(null);
    setCsvResults(null);
  };

  // Close handlers for dialogs
  const handleCloseQuestionDialog = () => {
    setCreateQuestionOpen(false);
    setEditQuestionOpen(false);
    setTagsInput('');
    setPastPapersInput('');
  };

  const handleCloseChapterDialog = () => {
    setCreateChapterOpen(false);
    setEditChapterOpen(false);
    setEditingChapter(null);
  };

  // Permission check
  if (!canRead('question_bank')) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            maxWidth: 420,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Block sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" fontWeight="500" gutterBottom color="text.primary">
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            You don&apos;t have permission to view Question Bank.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Contact your administrator for access.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const writable = canWrite('question_bank');
  const subjectName = subjects.find(s => s._id === selectedSubject)?.name
    || (activeBank ? nameOf(activeBank.subjectId) : '');
  const className = classes.find(c => c._id === selectedClass)?.name || '';
  const bankChapters = chapters.length;
  const bankQuestions = chapters.reduce((sum, c) => sum + (c.questionCount || 0), 0);

  return (
    <Box>
      {/* Page header */}
      {inBank ? (
        <Box sx={{ mb: 2.5 }}>
          <Button
            size="small"
            startIcon={<ArrowBackRounded />}
            onClick={() => openScope('', '')}
            sx={{ textTransform: 'none', color: 'text.secondary', ml: -1, mb: 0.5 }}
          >
            All question banks
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" fontWeight={700} color="text.primary" noWrap>
                {subjectName || <Skeleton width={180} />}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {[className && fmtClassName(className),
                  activeBank && !chaptersLoading && `${bankChapters} chapter${bankChapters === 1 ? '' : 's'}`,
                  activeBank && !chaptersLoading && `${bankQuestions} question${bankQuestions === 1 ? '' : 's'}`,
                ].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
            {writable && activeBank && (
              <>
                <Tooltip title="Bank options">
                  <IconButton onClick={(e) => setBankMenuAnchor(e.currentTarget)} sx={{ color: 'text.secondary' }}>
                    <MoreHoriz />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={bankMenuAnchor}
                  open={!!bankMenuAnchor}
                  onClose={() => setBankMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem
                    onClick={() => { setBankMenuAnchor(null); handleDeleteBank(activeBankId as string); }}
                    sx={{ color: 'error.main' }}
                  >
                    <ListItemIcon sx={{ color: 'inherit' }}><DeleteOutline fontSize="small" /></ListItemIcon>
                    Delete question bank
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} color="text.primary">
              Question Bank
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Questions for every class and subject, organised by chapter
            </Typography>
          </Box>
          {writable && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateBank} sx={{ borderRadius: 2 }}>
              New question bank
            </Button>
          )}
        </Box>
      )}

      {/* Class / subject: narrows the overview, and switches banks once both are picked */}
      <QuestionBankFilters
        isSystemAdmin={!!isSystemAdmin}
        centers={centers}
        classes={visibleFilterClasses}
        subjects={visibleFilterSubjects}
        selectedCenter={selectedCenter}
        selectedClass={selectedClass}
        selectedSubject={selectedSubject}
        onCenterChange={handleCenterChange}
        onClassChange={handleClassChange}
        onSubjectChange={handleSubjectChange}
      />

      {!inBank ? (
        <BankOverview
          banks={overviewBanks}
          total={overviewTotal}
          loading={overviewLoading}
          loadingMore={overviewLoadingMore}
          classFiltered={!!selectedClass}
          canWrite={writable}
          onOpen={handleOpenBank}
          onCreate={handleCreateBank}
          onLoadMore={() => fetchOverview(overviewPage + 1)}
        />
      ) : bankLoading && !activeBank ? (
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: '14px' }} />
      ) : !activeBank ? (
        <EmptyState
          icon={<LibraryBooksOutlined />}
          title={`No ${subjectName || 'question'} bank for ${className ? fmtClassName(className) : 'this class'} yet`}
          description="Create it to start adding chapters and questions."
          action={writable ? {
            label: creatingHere ? 'Creating…' : 'Create question bank',
            icon: <AddIcon />,
            onClick: () => { if (!creatingHere) handleCreateBankHere(); },
          } : undefined}
          secondaryAction={{ label: 'All question banks', onClick: () => openScope('', '') }}
          sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.paper' }}
        />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '260px minmax(0, 1fr)', lg: '300px minmax(0, 1fr)' },
            gap: { xs: 2, md: 3 },
            alignItems: 'start',
          }}
        >
          <ChapterSidebar
            chapters={chapters}
            selectedChapterId={selectedChapter?._id || null}
            loading={chaptersLoading}
            canWrite={writable}
            onSelectChapter={handleSelectChapter}
            onAddChapter={() => handleCreateChapter(activeBank)}
            onEditChapter={handleEditChapter}
            onDeleteChapter={handleDeleteChapter}
          />

          {selectedChapter ? (
            <QuestionPane
              chapter={selectedChapter}
              questions={questions}
              pagination={pagination}
              searchQuery={searchQuery}
              filterType={filterType}
              filterImportance={filterImportance}
              loading={questionsLoading}
              searching={searching}
              searchScope={searchScope}
              onSearchScopeChange={handleScopeChange}
              canWrite={writable}
              onAddQuestion={handleCreateQuestion}
              onUploadCSV={handleOpenCSVUpload}
              onSearchChange={handleSearchChange}
              onTypeChange={handleTypeChange}
              onImportanceChange={handleImportanceChange}
              onClearFilters={handleClearFilters}
              onViewQuestion={handleViewQuestion}
              onEditQuestion={handleEditQuestion}
              onDeleteQuestion={handleDeleteQuestion}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              getImportanceColor={getImportanceColor}
            />
          ) : chaptersLoading ? (
            <Skeleton variant="rounded" height={320} sx={{ borderRadius: '14px' }} />
          ) : (
            <EmptyState
              icon={<MenuBookOutlined />}
              title="Start with a chapter"
              description="Chapters keep questions in the order they are taught. Add the first one, then fill it with questions."
              action={writable ? { label: 'Add chapter', icon: <AddIcon />, onClick: () => handleCreateChapter(activeBank) } : undefined}
              sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.paper' }}
            />
          )}
        </Box>
      )}

      {/* Dialogs */}
      <CreateBankDialog
        open={createBankOpen}
        classes={visibleModalClasses}
        modalSubjects={visibleModalSubjects}
        formData={bankFormData}
        onClose={() => setCreateBankOpen(false)}
        onFormChange={setBankFormData}
        onClassChange={fetchModalSubjects}
        onSubmit={handleSubmitBank}
      />

      <ChapterDialog
        open={createChapterOpen || editChapterOpen}
        isEdit={editChapterOpen}
        formData={chapterFormData}
        onClose={handleCloseChapterDialog}
        onFormChange={setChapterFormData}
        onSubmit={editChapterOpen ? handleUpdateChapter : handleSubmitChapter}
      />

      <QuestionDialog
        open={createQuestionOpen || editQuestionOpen}
        isEdit={editQuestionOpen}
        formData={questionFormData}
        tagsInput={tagsInput}
        pastPapersInput={pastPapersInput}
        onClose={handleCloseQuestionDialog}
        onFormChange={setQuestionFormData}
        onTagsInputChange={setTagsInput}
        onPastPapersInputChange={setPastPapersInput}
        onSubmit={editQuestionOpen ? handleUpdateQuestion : handleSubmitQuestion}
      />

      <ViewQuestionDialog
        open={viewQuestionOpen}
        question={viewingQuestion}
        onClose={() => setViewQuestionOpen(false)}
        getImportanceColor={getImportanceColor}
      />

      <CSVUploadDialog
        open={uploadCSVOpen}
        chapter={csvUploadChapter}
        csvFile={csvFile}
        uploading={csvUploading}
        results={csvResults}
        onClose={handleCloseCSVUpload}
        onFileChange={handleCSVFileChange}
        onDownloadSample={handleDownloadSampleCSV}
        onUpload={handleUploadCSV}
      />

      <ConfirmationDialog
        open={deleteBankDialogOpen}
        onClose={() => setDeleteBankDialogOpen(false)}
        onConfirm={confirmDeleteBank}
        title="Delete Question Bank?"
        message="Are you sure? This will delete all chapters and questions in this bank. This action cannot be undone."
        confirmText="Delete"
        confirmColor="error"
      />

      <ConfirmationDialog
        open={deleteChapterDialogOpen}
        onClose={() => setDeleteChapterDialogOpen(false)}
        onConfirm={confirmDeleteChapter}
        title="Delete Chapter?"
        message="Are you sure? This will delete all questions in this chapter. This action cannot be undone."
        confirmText="Delete"
        confirmColor="error"
      />

      <ConfirmationDialog
        open={deleteQuestionDialogOpen}
        onClose={() => setDeleteQuestionDialogOpen(false)}
        onConfirm={confirmDeleteQuestion}
        title="Delete Question?"
        message="Are you sure you want to delete this question? This action cannot be undone."
        confirmText="Delete"
        confirmColor="error"
      />
    </Box>
  );
};

export default QuestionBankPage;
